import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import OrderPrintContent from '../common/OrderPrintContent.js';
import { getOrders, getOrderById, cancelOrder, completeOrder } from '../../services/orderApi.js';
import useWaiterStore from '../../store/branchstore/branchWaiterStore.js';
import useOrderStore from '../../store/branchstore/branchOrdersStore.js';

const SalesWaiterSection = ({ waiterId, onChangeWaiter }) => {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');
  const [pendingSearch, setPendingSearch] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [currentPrintOrder, setCurrentPrintOrder] = useState(null);
  const [isPrintReady, setIsPrintReady] = useState(false);
  const printRef = useRef();

  const {
    waiters,
    hasLoaded: waitersHasLoaded,
    loadWaiters
  } = useWaiterStore((state) => ({
    waiters: state.waiters,
    hasLoaded: state.hasLoaded,
    loadWaiters: state.loadWaiters
  }));

  const updateOrderStatus = useOrderStore((state) => state.updateOrderStatus);

  useEffect(() => {
    if (!waitersHasLoaded) {
      loadWaiters();
    }
  }, [waitersHasLoaded, loadWaiters]);

  useEffect(() => {
    loadWaiterOrders();
  }, []);

  const getWaiterName = (waiterIdValue) => {
    const waiter = waiters.find((waiter) => waiter.id === waiterIdValue);
    return waiter ? waiter.name : `#${waiterIdValue}`;
  };

  const orderMatchesSearch = (order, searchQuery) => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return true;

    const waiterName = getWaiterName(order.waiter_id).toLowerCase();
    return (
      order.id?.toString().toLowerCase().includes(query) ||
      order.total?.toString().toLowerCase().includes(query) ||
      waiterName.includes(query) ||
      (order.status || '').toLowerCase().includes(query)
    );
  };

  const filteredPendingOrders = useMemo(
    () => pendingOrders.filter((order) => orderMatchesSearch(order, pendingSearch)),
    [pendingOrders, pendingSearch, waiters]
  );

  const loadWaiterOrders = async () => {
    try {
      setOrdersLoading(true);
      setOrdersError('');

      const response = await getOrders();
      const orders = response.orders || [];

      const pending = orders.filter((order) => (
        order.order_type === 'waiter' &&
        order.status !== 'completed' &&
        order.status !== 'cancelled'
      ));

      const pendingWithItems = await Promise.all(
        pending.map(async (baseOrder) => {
          try {
            const detailed = await getOrderById(baseOrder.id);
            return {
              ...baseOrder,
              items: detailed.order?.items || []
            };
          } catch (itemError) {
            console.error('Failed to load pending order items for order', baseOrder.id, itemError);
            return { ...baseOrder, items: [] };
          }
        })
      );

      setPendingOrders(pendingWithItems);
    } catch (error) {
      setOrdersError('Failed to load waiter orders');
      console.error('Error loading waiter orders:', error);
    } finally {
      setOrdersLoading(false);
    }
  };

  const printHandler = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Receipt-${new Date().getTime()}`,
    pageStyle: `
      @page {
        size: 80mm auto;
        margin: 0;
      }
      body {
        margin: 0;
        padding: 0;
      }
    `,
    onAfterPrint: () => {
      setCurrentPrintOrder(null);
      setIsPrintReady(false);
    }
  });

  useEffect(() => {
    if (!currentPrintOrder || !isPrintReady) {
      return;
    }

    const timer = window.setTimeout(() => {
      if (printRef.current) {
        printHandler();
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [currentPrintOrder, isPrintReady, printHandler]);

  const handlePrint = (order) => {
    if (!order || !order.id) return;
    setCurrentPrintOrder(order);
    setIsPrintReady(false);
  };

  const handleComplete = async (orderId) => {
    try {
      setActionLoading(true);
      setActionError('');
      await completeOrder(orderId);
      updateOrderStatus(orderId, { status: 'completed' });
      await loadWaiterOrders();
    } catch (error) {
      setActionError(error.message || 'Failed to complete order');
      console.error('Error completing order:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async (orderId) => {
    try {
      setActionLoading(true);
      setActionError('');
      await cancelOrder(orderId);
      updateOrderStatus(orderId, { status: 'cancelled' });
      await loadWaiterOrders();
    } catch (error) {
      setActionError(error.message || 'Failed to cancel order');
      console.error('Error cancelling order:', error);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-3xl bg-white p-6 shadow-sm">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Pending Waiter Orders</h2>
            <p className="mt-2 text-sm text-gray-600">Orders waiting for completion from the sales screen</p>
          </div>
          <input
            type="text"
            value={pendingSearch}
            onChange={(e) => setPendingSearch(e.target.value)}
            placeholder="Search by order #, waiter, status or amount"
            className="w-full max-w-md rounded-2xl border border-gray-300 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />
        </div>

        {ordersError && (
          <div className="mb-4 rounded-2xl bg-red-50 p-3 text-sm text-red-700">{ordersError}</div>
        )}

        {ordersLoading ? (
          <div className="text-center text-gray-500">Loading pending orders...</div>
        ) : filteredPendingOrders.length === 0 ? (
          <div className="rounded-2xl bg-gray-50 p-6 text-center">
            <p className="text-sm text-gray-500">No pending waiter orders match the current filter</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 h-[550px] overflow-y-auto scrollbar">
            {filteredPendingOrders.map((order) => (
              <div
                key={order.id}
                className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-4 shadow-sm"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-600">Order</div>
                    <h3 className="mt-1 text-lg font-semibold text-gray-900">#{order.order_number || order.id}</h3>
                  </div>
                  <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs font-semibold text-yellow-800">
                    {order.status || 'pending'}
                  </span>
                </div>

                <div className="mt-3 text-sm text-gray-600">
                  <p className="text-lg text-black">
                    <strong>Waiter:</strong> {order.waiter_id ? getWaiterName(order.waiter_id) : 'N/A'}
                  </p>
                  <p className="mt-1 text-lg text-black">
                    <strong>Amount:</strong> {order.total || 0}
                  </p>
                  <p className="mt-1">
                    <strong>Time:</strong> {order.created_at ? new Date(order.created_at).toLocaleString() : '-'}
                  </p>
                </div>

                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-gray-900">Items</h4>
                  <div className="mt-3 max-h-44 overflow-y-auto rounded-2xl border border-gray-200 bg-slate-50 p-3">
                    {order.items && order.items.length > 0 ? (
                      order.items.map((item, index) => (
                        <div key={`${item.product_name || item.id || 'item'}-${index}`} className="flex items-center justify-between gap-3 rounded-xl bg-white px-3 py-2 text-sm shadow-sm">
                          <span className="font-medium text-gray-800">
                            {item.product_name || item.variant_name || item.name || 'Item'}
                          </span>
                          <span className="text-slate-600">{item.quantity || 0}x</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">No item details available.</p>
                    )}
                  </div>
                </div>

                <div className="mt-4 flex items-center gap-2 border-t border-gray-200 pt-4">
                  <button
                    onClick={() => handlePrint(order)}
                    className="flex items-center gap-1 rounded-lg bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 transition"
                    title="Print receipt"
                  >
                    🖨️
                  </button>
                  <button
                    onClick={() => handleComplete(order.id)}
                    className="flex items-center gap-1 rounded-lg bg-green-50 px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-100 transition"
                    title="Mark as completed"
                  >
                    ✓
                  </button>
                  <button
                    onClick={() => handleCancel(order.id)}
                    className="flex items-center gap-1 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100 transition"
                    title="Cancel order"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {actionError && (
        <div className="rounded-2xl bg-red-50 p-3 text-sm text-red-700">{actionError}</div>
      )}

      {currentPrintOrder && (
        <div
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            width: '80mm',
            height: 'auto',
            visibility: 'hidden',
            overflow: 'visible',
            pointerEvents: 'none',
            zIndex: -1
          }}
        >
          <OrderPrintContent
            ref={printRef}
            order={currentPrintOrder}
            onReady={() => setIsPrintReady(true)}
          />
        </div>
      )}
    </div>
  );
};

export default SalesWaiterSection;
